import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Check, AlertCircle } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ST120FormPage = () => {
  const { user, token } = useAuth();
  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingForm, setExistingForm] = useState(null);
  const [formData, setFormData] = useState({
    // Certificate Type
    certificate_type: 'blanket', // 'single' or 'blanket'
    
    // Seller Information (Apollo Shades - pre-filled)
    seller_name: 'Apollo Shades',
    seller_address: '292 Railroad Ave',
    seller_city: 'Brooklyn',
    seller_state: 'NY',
    seller_zip: '11249',
    
    // Purchaser Information
    purchaser_name: '',
    purchaser_address: '',
    purchaser_city: '',
    purchaser_state: '',
    purchaser_zip: '',
    
    // Business Information
    business_type: '', // "engaged in the business of"
    principally_sell: '', // "and principally sell"
    
    // Part 1 - NYS Registered Vendor
    is_nys_vendor: true,
    vendor_type: 'regular', // 'regular' or 'temporary'
    sales_tax_id: '',
    temp_vendor_expires: '',
    
    // Part 1 - Purchasing Options
    purchasing_tangible_property: true,
    purchasing_service: false,
    purchasing_food: false,
    
    // Part 2 - Non-NYS Purchaser
    is_non_nys: false,
    registered_jurisdiction: '',
    registration_number: '',
    non_nys_direct_delivery: false,
    non_nys_resold_outside: false,
    
    // Signature
    signatory_name: '',
    signatory_title: '',
  });

  useEffect(() => {
    checkExistingForm();
  }, [token]);

  const checkExistingForm = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/st120/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_st120) {
        setExistingForm(response.data);
      }
    } catch (error) {
      console.error('Error checking ST-120 status:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }

    // Validate required fields
    if (!formData.purchaser_name || !formData.purchaser_address || !formData.purchaser_city || 
        !formData.purchaser_state || !formData.purchaser_zip) {
      toast.error('Please complete all purchaser information fields');
      return;
    }

    if (formData.is_nys_vendor && !formData.sales_tax_id) {
      toast.error('Please enter your NYS Sales Tax ID number');
      return;
    }

    if (formData.is_non_nys && !formData.registered_jurisdiction) {
      toast.error('Please enter the jurisdiction where you are registered');
      return;
    }

    setSubmitting(true);
    
    try {
      const signatureData = sigCanvas.current.toDataURL('image/png');
      const submitData = {
        ...formData,
        signature_data: signatureData,
        signature_date: new Date().toISOString().split('T')[0],
      };

      await axios.post(`${API_URL}/st120/submit`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('ST-120 form submitted successfully!');
      checkExistingForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="container-lg text-center">
            <FileText className="w-16 h-16 mx-auto text-neutral-300 mb-6" />
            <h1 className="text-2xl font-serif mb-4">Sign In Required</h1>
            <p className="text-neutral-600 mb-6">Please sign in to access the ST-120 form.</p>
            <Button onClick={() => window.location.href = '/login'} className="btn-primary">
              Sign In
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (existingForm?.has_st120) {
    return (
      <Layout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="container-lg max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-serif mb-4">ST-120 On File</h1>
              <p className="text-neutral-600 mb-8">
                Your New York State Resale Certificate is on file with Apollo Shades.
              </p>
              
              <div className="bg-neutral-50 rounded-lg p-6 text-left mb-8">
                <h3 className="font-medium mb-4">Certificate Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-neutral-500">Business Name:</span>
                    <p className="font-medium">{existingForm.form?.purchaser_name}</p>
                  </div>
                  <div>
                    <span className="text-neutral-500">Tax ID:</span>
                    <p className="font-medium">{existingForm.form?.sales_tax_id || existingForm.form?.registration_number}</p>
                  </div>
                  <div>
                    <span className="text-neutral-500">Certificate Type:</span>
                    <p className="font-medium capitalize">{existingForm.form?.certificate_type}</p>
                  </div>
                  <div>
                    <span className="text-neutral-500">Signed Date:</span>
                    <p className="font-medium">{new Date(existingForm.signed_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-neutral-500">
                Need to update your certificate? Contact us at{' '}
                <a href="mailto:trade@apolloshades.com" className="text-amber-700 hover:underline">
                  trade@apolloshades.com
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <button 
          onClick={() => window.history.back()} 
          className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </button>
      </div>

      {/* Hero */}
      <section className="pt-8 pb-8 bg-neutral-50">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <img src="https://www.tax.ny.gov/images/nys-logo.png" alt="NY State" className="h-12" onError={(e) => e.target.style.display='none'} />
              <div>
                <p className="text-sm text-neutral-500">New York State Department of Taxation and Finance</p>
                <p className="text-sm text-neutral-500">New York State and Local Sales and Use Tax</p>
              </div>
            </div>
            <h1 className="mt-4 text-neutral-900">Resale Certificate</h1>
            <p className="text-sm text-neutral-500 mt-2">Form ST-120</p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="section-padding">
        <div className="container-lg max-w-4xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Seller Information */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Seller Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Name of seller</Label>
                  <Input value={formData.seller_name} disabled className="mt-1 bg-neutral-50" />
                </div>
                <div className="md:col-span-2">
                  <Label>Street address</Label>
                  <Input value={formData.seller_address} disabled className="mt-1 bg-neutral-50" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={formData.seller_city} disabled className="mt-1 bg-neutral-50" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State</Label>
                    <Input value={formData.seller_state} disabled className="mt-1 bg-neutral-50" />
                  </div>
                  <div>
                    <Label>ZIP code</Label>
                    <Input value={formData.seller_zip} disabled className="mt-1 bg-neutral-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Purchaser Information */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Purchaser Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Name of purchaser *</Label>
                  <Input
                    value={formData.purchaser_name}
                    onChange={(e) => setFormData({...formData, purchaser_name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Street address *</Label>
                  <Input
                    value={formData.purchaser_address}
                    onChange={(e) => setFormData({...formData, purchaser_address: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <Input
                    value={formData.purchaser_city}
                    onChange={(e) => setFormData({...formData, purchaser_city: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State *</Label>
                    <Input
                      value={formData.purchaser_state}
                      onChange={(e) => setFormData({...formData, purchaser_state: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>ZIP code *</Label>
                    <Input
                      value={formData.purchaser_zip}
                      onChange={(e) => setFormData({...formData, purchaser_zip: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Certificate Type */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Mark an X in the appropriate box:</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="certificate_type"
                    checked={formData.certificate_type === 'single'}
                    onChange={() => setFormData({...formData, certificate_type: 'single'})}
                    className="w-4 h-4"
                  />
                  <span>Single-use certificate</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="certificate_type"
                    checked={formData.certificate_type === 'blanket'}
                    onChange={() => setFormData({...formData, certificate_type: 'blanket'})}
                    className="w-4 h-4"
                  />
                  <span>Blanket certificate</span>
                </label>
                <p className="text-sm text-neutral-500 italic ml-7">Temporary vendors must issue a single-use certificate.</p>
              </div>
            </div>

            {/* Business Description */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">To the purchaser:</h2>
              <p className="text-sm text-neutral-600 mb-4">
                The tangible personal property or service being purchased is being purchased either for resale in its present 
                form or for resale as a physical component part of tangible personal property; or for use in performing a 
                taxable service where the property will become a physical component part of the property on which the service 
                is performed or the property will actually be transferred to the purchaser of the service.
              </p>
              <div className="space-y-4">
                <div>
                  <Label>I am engaged in the business of *</Label>
                  <Input
                    value={formData.business_type}
                    onChange={(e) => setFormData({...formData, business_type: e.target.value})}
                    placeholder="e.g., interior design, window treatment retail, construction"
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>and principally sell *</Label>
                  <Input
                    value={formData.principally_sell}
                    onChange={(e) => setFormData({...formData, principally_sell: e.target.value})}
                    placeholder="e.g., window treatments, blinds, shades, interior furnishings"
                    required
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-sm text-neutral-500 mt-4 italic">
                (Contractors may not use this certificate to purchase materials and supplies.)
              </p>
            </div>

            {/* Vendor Type Selection */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Select your vendor status:</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="vendor_status"
                    checked={formData.is_nys_vendor}
                    onChange={() => setFormData({...formData, is_nys_vendor: true, is_non_nys: false})}
                    className="w-4 h-4"
                  />
                  <span>I am a registered New York State sales tax vendor</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="vendor_status"
                    checked={formData.is_non_nys}
                    onChange={() => setFormData({...formData, is_nys_vendor: false, is_non_nys: true})}
                    className="w-4 h-4"
                  />
                  <span>I am a non-New York State purchaser (not required to register in NYS)</span>
                </label>
              </div>
            </div>

            {/* Part 1 - NYS Registered Vendors */}
            {formData.is_nys_vendor && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4 border-b border-blue-200 pb-2">
                  Part 1 - To be completed by registered New York State sales tax vendors
                </h2>
                
                <div className="space-y-4">
                  <p className="font-medium">I certify that I am:</p>
                  
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="vendor_type"
                      checked={formData.vendor_type === 'regular'}
                      onChange={() => setFormData({...formData, vendor_type: 'regular'})}
                      className="w-4 h-4 mt-1"
                    />
                    <span className="text-sm">
                      a New York State vendor (including a hotel or short-term rental unit operator or a dues or admissions recipient), show vendor or entertainment vendor.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="vendor_type"
                      checked={formData.vendor_type === 'temporary'}
                      onChange={() => setFormData({...formData, vendor_type: 'temporary', certificate_type: 'single'})}
                      className="w-4 h-4 mt-1"
                    />
                    <span className="text-sm">a New York State temporary vendor.</span>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label>My valid sales tax identification number is *</Label>
                      <Input
                        value={formData.sales_tax_id}
                        onChange={(e) => setFormData({...formData, sales_tax_id: e.target.value})}
                        placeholder="Enter your NYS Sales Tax ID"
                        required
                        className="mt-1"
                      />
                    </div>
                    {formData.vendor_type === 'temporary' && (
                      <div>
                        <Label>and expires on</Label>
                        <Input
                          type="date"
                          value={formData.temp_vendor_expires}
                          onChange={(e) => setFormData({...formData, temp_vendor_expires: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <p className="font-medium mb-3">I am purchasing:</p>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.purchasing_tangible_property}
                          onChange={(e) => setFormData({...formData, purchasing_tangible_property: e.target.checked})}
                          className="w-4 h-4 mt-1"
                        />
                        <div className="text-sm">
                          <span className="font-medium">A.</span> tangible personal property (other than motor fuel or diesel motor fuel)
                          <ul className="list-disc ml-6 mt-1 text-neutral-600">
                            <li>for resale in its present form or for resale as a physical component part of tangible personal property;</li>
                            <li>for use in performing taxable services where the property will become a physical component part of the property upon which the services will be performed, or the property will actually be transferred to the purchaser of the taxable service in conjunction with the performance of the service.</li>
                          </ul>
                        </div>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.purchasing_service}
                          onChange={(e) => setFormData({...formData, purchasing_service: e.target.checked})}
                          className="w-4 h-4 mt-1"
                        />
                        <span className="text-sm">
                          <span className="font-medium">B.</span> a service for resale, including the servicing of tangible personal property held for sale.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.purchasing_food}
                          onChange={(e) => setFormData({...formData, purchasing_food: e.target.checked})}
                          className="w-4 h-4 mt-1"
                        />
                        <span className="text-sm">
                          <span className="font-medium">C.</span> restaurant-type food, heated food, or heated drink for resale.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Part 2 - Non-NYS Purchasers */}
            {formData.is_non_nys && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4 border-b border-amber-200 pb-2">
                  Part 2 - To be completed by non-New York State purchasers
                </h2>
                
                <div className="space-y-4">
                  <p className="text-sm text-neutral-700">
                    I certify that I am not registered nor am I required to be registered as a New York State sales tax vendor.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>I am registered to collect sales tax or VAT in *</Label>
                      <Input
                        value={formData.registered_jurisdiction}
                        onChange={(e) => setFormData({...formData, registered_jurisdiction: e.target.value})}
                        placeholder="State/Jurisdiction"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>and have been issued the following registration number</Label>
                      <Input
                        value={formData.registration_number}
                        onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                        placeholder="Registration number (or 'not applicable')"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-neutral-500 italic">
                    If sales tax or VAT registration is not required and a registration number is not issued by your home jurisdiction, 
                    indicate the location of your business and write "not applicable" on the line requesting the registration number.
                  </p>

                  <div className="mt-4">
                    <p className="font-medium mb-3">I am purchasing:</p>
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.non_nys_direct_delivery}
                          onChange={(e) => setFormData({...formData, non_nys_direct_delivery: e.target.checked})}
                          className="w-4 h-4 mt-1"
                        />
                        <span className="text-sm">
                          <span className="font-medium">D.</span> tangible personal property (other than motor fuel or diesel motor fuel) for resale, 
                          and it is being delivered directly by the seller to my customer or to an unaffiliated fulfillment services provider in New York State.
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.non_nys_resold_outside}
                          onChange={(e) => setFormData({...formData, non_nys_resold_outside: e.target.checked})}
                          className="w-4 h-4 mt-1"
                        />
                        <span className="text-sm">
                          <span className="font-medium">E.</span> tangible personal property for resale that will be resold from a business located outside New York State.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Certification */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b border-red-200 pb-2">Certification</h2>
              <div className="space-y-4 text-sm text-neutral-700">
                <p>
                  I certify that the above statements are true, complete, and correct, and that no material information has been omitted.
                </p>
                <p>
                  I make these statements and issue this exemption certificate with the knowledge that this document provides evidence 
                  that state and local sales or use taxes do not apply to a transaction or transactions for which I tendered this document 
                  and that <strong>willfully issuing this document with the intent to evade any such tax may constitute a felony or other crime 
                  under New York State Law, punishable by a substantial fine and a possible jail sentence.</strong>
                </p>
                <p>
                  I understand that this document is required to be filed with, and delivered to, the vendor as agent for the Tax Department 
                  for the purposes of Tax Law section 1838 and is deemed a document required to be filed with the Tax Department for the 
                  purpose of prosecution of offenses.
                </p>
                <p>
                  I also understand that the Tax Department is authorized to investigate the validity of tax exclusions or exemptions claimed 
                  and the accuracy of any information entered on this document.
                </p>
              </div>
            </div>

            {/* Signature */}
            <div className="bg-white border border-neutral-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4 border-b pb-2">Signature</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label>Name and title of owner, partner, or authorized person of purchaser (Type or print) *</Label>
                  <Input
                    value={formData.signatory_name}
                    onChange={(e) => setFormData({...formData, signatory_name: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={formData.signatory_title}
                    onChange={(e) => setFormData({...formData, signatory_title: e.target.value})}
                    required
                    placeholder="e.g., Owner, President, Manager"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Signature of owner, partner, or authorized person of purchaser *</Label>
                <p className="text-sm text-neutral-500 mb-3">
                  Please sign in the box below using your mouse or touchscreen
                </p>
                <div className="border-2 border-neutral-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      width: 600,
                      height: 150,
                      className: 'signature-canvas w-full',
                      style: { width: '100%', height: '150px' }
                    }}
                    backgroundColor="white"
                  />
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-neutral-500">
                    Date prepared: {new Date().toLocaleDateString()}
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                    Clear Signature
                  </Button>
                </div>
              </div>

              <div className="mt-6 p-4 bg-red-100 border border-red-300 rounded-lg">
                <p className="text-sm font-semibold text-red-800 text-center">
                  Substantial penalties will result from misuse of this certificate.
                </p>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => window.history.back()}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? 'Submitting...' : 'Submit ST-120 Form'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default ST120FormPage;
